import { OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { Subscription } from 'rxjs';
import { deepEqual } from '@client/app/shared/utils/object-utils';
import { ArgsInterface } from '@monsido_services/services/mon-translate/dist/translate.interface';
import { TranslateService } from '@client/app/services/translate/translate.service';

@Pipe({
    name: 'translateWithPlural',
    pure: false,
    standalone: false,
})
export class TranslateWithPluralPipe implements PipeTransform, OnDestroy {
    /**
     * Subscription that handles language changes
     *
     * @private
     */
    private onLangChange: Subscription | null = null;

    /**
     * {value} is used to store the result from the transform method after translations are applied
     *
     * @private
     */
    private value: string = '';

    /**
     * {lastText} and {lastArgs} are used for comparison between current text and args values and
     * previous text and args value; they can determine if the current inputs of the transform method are
     * the same as last ones so as to avoid unnecessary computations.
     *
     * @private
     */
    private lastNumber: string | number | null = null;
    private lastText: string | null = null;
    private lastTextPlural: string | null = null;
    private lastArgs: ArgsInterface | null = null;

    /**
     * {text} {args} and {context} are used to pass the values to the constructor subscription
     *
     * @private
     */
    private number: string | number = 0;
    private text = '';
    private textPlural = '';
    private args: ArgsInterface | null = null;
    private context: string | null = null;

    constructor (
        private translateService: TranslateService,
    ) {
        this.onLangChange = this.translateService.onLanguageChange$().subscribe({
            next: () => {
                if (this.lastText) {
                    this.lastText = null;
                    this.lastTextPlural = null;
                    this.lastNumber = null;
                    this.updateValue(this.number = 0, this.text = '', this.textPlural = '', this.args, this.context);
                }
            },
        });
    }

    ngOnDestroy (): void {
        this.dispose();
    }

    transform (text: string | undefined, n: string | number, textPlural: string, args?: ArgsInterface, context?: string): string {
        if (!text || !textPlural) {
            return text || '';
        }

        // capturing values here to use in the constructor subscription
        this.args = args || null;
        this.context = context || null;
        this.text = text;
        this.textPlural = textPlural;
        this.number = n;
        this.lastNumber = n;
        // if we ask another time for the same text, return the last value
        if (deepEqual(args, this.lastArgs) && this.lastText === text) {
            return this.value;
        }

        // store the text, in case it changes
        this.lastText = text;

        // store args, in case they change
        this.lastArgs = args || null;

        this.updateValue(Number(n), text, textPlural, args || null, context || null);

        return this.value;
    }

    /**
     * update the text to be translated, stored in the {value} prop
     *
     * @param text
     * @param args
     * @param context
     *
     * @private
     */
    private updateValue (
        n: number,
        text: string,
        textPlural: string,
        args: ArgsInterface | null,
        context: string | null,
    ): void {
        const res = this.translateService.getPlural(n, text, textPlural, args || {}, context || '');

        this.value = res !== undefined ? res : text;
        this.lastText = text;
    }

    /**
     * Clean existing subscriptions
     *
     * @private
     */
    private dispose (): void {
        if (this.onLangChange !== null) {
            this.onLangChange.unsubscribe();
            this.onLangChange = null;
        }
    }
}
